package com.dotdrives.web.pages.updates

import com.dotdrives.controller.UpdatesFilters
import com.dotdrives.design.DotAvatarPalette
import com.dotdrives.model.ReportStatus
import com.dotdrives.model.ReportSummary
import com.dotdrives.web.ui.DotAction
import com.dotdrives.web.ui.DotBanner
import com.dotdrives.web.ui.DotEmptyState
import com.dotdrives.web.ui.DotPill
import com.dotdrives.web.ui.DotSkeleton
import react.FC
import react.Key
import react.Props
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.span
import web.cssom.ClassName
import web.html.ButtonType
import web.html.button as buttonType
import kotlin.time.Clock

external interface UpdatesListProps : Props {
    var totalCount: Int
    var unreadCount: Int
    var hasMore: Boolean
    var loadingMore: Boolean
    var onLoadMore: () -> Unit
    var loading: Boolean
    var reports: List<ReportSummary>
    var visibleReports: List<ReportSummary>
    var filters: UpdatesFilters
    var selectedReportId: String?
    var error: String?
    var onFilterChange: (UpdatesFilters) -> Unit
    var onSelect: (String) -> Unit
    var onRetry: () -> Unit
    var onNewUpdate: () -> Unit
}

val UpdatesList = FC<UpdatesListProps> { props ->
    val unreadCount = props.unreadCount

    div {
        className = ClassName("updates-list-pane")
        div {
            className = ClassName("updates-list-head")
            div {
                className = ClassName("updates-list-head-row")
                div { className = ClassName("updates-list-title"); +"Giver updates" }
                button {
                    type = ButtonType.buttonType
                    className = ClassName("dot-button dot-button-primary updates-list-new-button")
                    onClick = { props.onNewUpdate() }
                    +"+ New update"
                }
            }
            div {
                className = ClassName("updates-filter-pills")
                button {
                    type = ButtonType.buttonType
                    className = ClassName(
                        "updates-filter-pill" + if (!props.filters.onlyUnread) " updates-filter-pill-active" else "",
                    )
                    onClick = { props.onFilterChange(props.filters.copy(onlyUnread = false)) }
                    +"All · ${props.totalCount}"
                }
                button {
                    type = ButtonType.buttonType
                    className = ClassName(
                        "updates-filter-pill" + if (props.filters.onlyUnread) " updates-filter-pill-active" else "",
                    )
                    onClick = { props.onFilterChange(props.filters.copy(onlyUnread = true)) }
                    +"Unread · $unreadCount"
                }
            }
        }

        props.error?.let { error ->
            DotBanner {
                message = error
                onRetry = props.onRetry
                className = "updates-inline-banner"
            }
        }

        div {
            className = ClassName("updates-list-body")
            when {
                props.loading && props.reports.isEmpty() && props.error == null -> UpdatesListSkeleton()
                props.totalCount == 0 && !props.loading && props.error == null -> UpdatesListEmpty {
                    onNewUpdate = props.onNewUpdate
                }
                props.visibleReports.isEmpty() && !props.hasMore && !props.loading && props.error == null -> UpdatesListNoMatches {
                    onClearFilters = { props.onFilterChange(UpdatesFilters()) }
                }
                else -> {
                    div {
                        className = ClassName("updates-list-rows")
                        props.visibleReports.forEach { report ->
                            UpdatesRow {
                                key = report.id.unsafeCast<Key>()
                                this.report = report
                                selected = report.id == props.selectedReportId
                                onClick = { props.onSelect(report.id) }
                            }
                        }
                        if (props.hasMore) button {
                            type = ButtonType.buttonType
                            className = ClassName("dot-button dot-button-secondary")
                            disabled = props.loadingMore
                            onClick = { props.onLoadMore() }
                            +if (props.loadingMore) "Loading…" else "Load more updates"
                        }

                    }
                }
            }
        }
    }
}

private external interface UpdatesRowProps : Props {
    var report: ReportSummary
    var selected: Boolean
    var onClick: () -> Unit
}

private val UpdatesRow = FC<UpdatesRowProps> { props ->
    val report = props.report
    val (bg, _) = DotAvatarPalette.tintFor(report.assigneeUserId)
    val avatarStyle = js("({})")
    avatarStyle.backgroundColor = bg
    val statusStyle = reportStatusStyle(report.status)

    button {
        type = ButtonType.buttonType
        className = ClassName(
            "updates-row" + if (props.selected) " updates-row-selected" else "",
        )
        onClick = { props.onClick() }
        div {
            className = ClassName("updates-row-avatar")
            style = avatarStyle
            +updateInitials(report.assigneeName)
        }
        div {
            className = ClassName("updates-row-copy")
            div {
                className = ClassName("updates-row-top")
                span { className = ClassName("updates-row-title"); +report.title }
                report.latestAt?.let { at ->
                    span { className = ClassName("updates-row-when"); +relativeTimeLabel(at, Clock.System.now()) }
                }
            }
            div { className = ClassName("updates-row-preview"); +report.latestPreview.ifBlank { "No updates posted yet" } }
            div {
                className = ClassName("updates-row-meta")
                DotPill { text = statusStyle.label; tone = statusStyle.tone }
                if (report.fileCount > 0) {
                    span { className = ClassName("updates-row-files"); +"📎 ${report.fileCount}" }
                }
                if (report.isUnread) {
                    span { className = ClassName("updates-row-unread-dot") }
                }
            }
        }
    }
}

private val UpdatesListSkeleton = FC<Props> {
    div {
        className = ClassName("updates-list-skeleton")
        repeat(5) { DotSkeleton { height = "76px" } }
    }
}

private external interface UpdatesListEmptyProps : Props {
    var onNewUpdate: () -> Unit
}

private val UpdatesListEmpty = FC<UpdatesListEmptyProps> { props ->
    div {
        className = ClassName("updates-list-empty-wrap")
        DotEmptyState {
            emoji = "📣"
            title = "No updates yet"
            body = "Updates are how the field talks to your givers. Invite a field representative, or write the first one yourself."
            primaryAction = DotAction("+ Write an update", props.onNewUpdate)
            secondaryAction = null
        }
    }
}

private external interface UpdatesListNoMatchesProps : Props {
    var onClearFilters: () -> Unit
}

/** Zero rows under the "Unread" quick filter is a different fact than zero updates ever
 *  existing -- see [UpdatesListEmpty] for that state instead. */
private val UpdatesListNoMatches = FC<UpdatesListNoMatchesProps> { props ->
    div {
        className = ClassName("updates-list-empty-wrap")
        DotEmptyState {
            emoji = "🔍"
            title = "No updates match this filter"
            body = "Try a different filter to see the full list."
            primaryAction = DotAction("Clear filter", props.onClearFilters)
            secondaryAction = null
        }
    }
}
